/**
 * TgDeclineModal
 * Modal for subcontractor to decline an assignment
 */

import { useState } from 'react'
import { X, AlertTriangle, Loader2 } from 'lucide-react'

interface TgDeclineModalProps {
  isOpen: boolean
  onClose: () => void
  subcontractorName: string
  assignmentTitle: string
  assignmentDate?: string
  onConfirm: (reason: string) => Promise<void>
}

export function TgDeclineModal({
  isOpen,
  onClose,
  subcontractorName,
  assignmentTitle,
  assignmentDate,
  onConfirm
}: TgDeclineModalProps) {
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return
    }

    setIsSubmitting(true)
    try {
      await onConfirm(reason.trim())
      onClose()
    } catch (error) {
      console.error('Error declining assignment:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setReason('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Avsäg uppdrag
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  {subcontractorName}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Assignment info */}
          <div className="bg-nature-50 dark:bg-nature-700 rounded-lg p-4">
            <p className="text-sm text-nature-700 dark:text-nature-300">
              <strong>{assignmentTitle}</strong>
              {assignmentDate && (
                <span className="block text-nature-500 dark:text-nature-400 mt-1">
                  {assignmentDate}
                </span>
              )}
            </p>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Om du avsäger dig detta uppdrag kommer admin att notifieras via SMS och uppdraget kan tilldelas en annan trädgårdsmästare.
            </p>
          </div>

          {/* Reason input */}
          <div>
            <label className="form-label">Anledning *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Beskriv varför du behöver avsäga dig uppdraget..."
              rows={3}
              className="input-field resize-none"
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={handleClose}
              className="btn-outline px-4 py-2"
              disabled={isSubmitting}
            >
              Avbryt
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting || !reason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Sparar...</span>
                </>
              ) : (
                <span>Avsäg uppdrag</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
